import React from "react";
import { Box, MenuItem, SxProps, Tooltip } from "@mui/material";
/* WhatsApp sunset 2026-03-14 — IconButton was only used for WhatsApp button */
import { useNavigate } from "react-router-dom";
import { BuildOutlined, ContentCopy, TuneOutlined, GroupWork as GroupWorkIcon, RestoreOutlined, ArchiveOutlined, PlaceOutlined } from "@mui/icons-material";
/* WhatsApp sunset 2026-03-14 — channel deactivated, web chat is sole channel */
// import whatsappLogo from "../../../shared/assets/images/whatsapp-logo.png";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import QrCodeIcon from "@mui/icons-material/QrCode";
import colors from "../../../shared/themes/colors";
import CameraIcon from "../../../shared/assets/icons/CameraIcon";
import OverflowTooltip from "../../../shared/components/OverflowTooltip";
import TableActionButton from "../../../shared/components/tables/TableActionButton";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import { Machine } from "../../machinery/models/models";
import useTranslation from "../../../shared/hooks/useTranslation";
import { getMachineDisplayName, getProductionYearDisplay, getBrandDisplayName, getMachineName } from "../../../shared/utils/machineUtils";
import SrStatusBadge from "../../machinery/components/SrStatusBadge";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";

// =====================================================================
//  HEALTH INDICATOR
// =====================================================================

const healthColors = {
  healthy: "#22C55E",
  attention: "#F59E0B",
  critical: "#EF4444",
};

const getHealthColor = (openServiceReportsCount: number): string => {
  if (openServiceReportsCount === 0) return healthColors.healthy;
  if (openServiceReportsCount <= 2) return healthColors.attention;
  return healthColors.critical;
};

interface MachineCardProps {
  machine: Machine;
  showActions?: boolean;
  isNew?: boolean;
  hideServiceBadge?: boolean;
  onQRCode?: (machine: Machine) => void;
  /* WhatsApp sunset 2026-03-14 — channel deactivated, web chat is sole channel */
  // onWhatsAppBot?: (machine: Machine) => void;
  onDecommission?: (machine: Machine) => void;
  onRestore?: (machine: Machine) => void;
  onEdit?: (machine: Machine) => void;
  onEditDetails?: (machine: Machine) => void;
  onCreateServiceRequest?: (machine: Machine) => void;
  onCreateServiceReport?: (machine: Machine) => void;
  onDuplicate?: (machine: Machine) => void;
  onAssignGroup?: (machine: Machine) => void;
  sx?: SxProps;
}

const MachineCard = ({
  /* WhatsApp sunset 2026-03-14 — channel deactivated, web chat is sole channel */
  machine, showActions, isNew, hideServiceBadge, onQRCode, /* onWhatsAppBot, */ onEdit, onEditDetails, onCreateServiceRequest, onCreateServiceReport, onDuplicate, onDecommission, onRestore, onAssignGroup, sx
}: MachineCardProps) => {
  const confirm = useConfirmation();
  const navigate = useNavigate();
  showActions = showActions || false;
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");

  const isClickable = showActions;
  const isDecommissioned = machine.isDecommissioned === true;

  const handleCardClick = () => {
    if (!isClickable) return;
    navigate(`/machinery/${machine.id}`);
  };

  const stopProp = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const healthColor = getHealthColor(machine.openServiceReportsCount);
  const healthTooltip = machine.openServiceReportsCount === 0
    ? translate("healthNoIssues")
    : translate("healthOpenReports", { count: machine.openServiceReportsCount });

  return (
    <Box
      className="flex flex-col self-start gap-2"
      onClick={handleCardClick}
      sx={{
        width: "100%",
        maxHeight: "560px",
        height: "100%",
        background: machine.serialNumber ? colors.secondary.gray98 : colors.secondary.gray96,
        border: isDecommissioned ? "1px dashed #D1D5DB" : (machine.serialNumber ? "1px solid #EEEEEF" : "1px solid #D5D6D9"),
        borderRadius: "8px",
        padding: "16px",
        boxShadow: machine.serialNumber ?
          "0 8px 32px 0 rgba(0,33,89,0.1)" :
          "0 8px 32px 0 rgba(0, 88, 237, 0.1)",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        opacity: isDecommissioned ? 0.65 : 1,
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        ...(isClickable ? {
          cursor: "pointer",
          "&:hover": {
            transform: "translateY(-2px)",
            opacity: isDecommissioned ? 0.85 : 1,
            boxShadow: machine.serialNumber
              ? "0 12px 40px 0 rgba(0,33,89,0.16), 0 2px 8px rgba(0,33,89,0.06)"
              : "0 12px 40px 0 rgba(0, 88, 237, 0.16), 0 2px 8px rgba(0,88,237,0.06)",
          },
        } : {}),
        ...sx
      }}
    >
      {/* NEW badge */}
      {isNew && (
        <Box
          sx={{
            position: "absolute",
            top: "12px",
            left: "12px",
            zIndex: 2,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(39, 174, 96, 0.08)",
            color: "#27AE60",
            border: "1px solid rgba(39, 174, 96, 0.25)",
            borderRadius: "10px",
            padding: "2px 10px",
            fontSize: "10px",
            fontWeight: 700,
            fontFamily: 'var(--font-sans)',
            textTransform: "uppercase",
            letterSpacing: "1.2px",
            lineHeight: "16px",
            whiteSpace: "nowrap",
            pointerEvents: "none",
            animation: "newMachinePulse 2s ease-in-out 3",
          }}
        >
          {translate("new")}
        </Box>
      )}

      {/* Health indicator dot -- hidden for decommissioned */}
      {showActions && !isDecommissioned && (
        <Tooltip title={healthTooltip} arrow placement="top">
          <Box
            sx={{
              position: "absolute",
              top: "12px",
              right: "12px",
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: healthColor,
              boxShadow: `0 0 6px ${healthColor}80`,
              zIndex: 1,
              transition: "transform 0.2s ease",
              "&:hover": {
                transform: "scale(1.3)",
              },
            }}
          />
        </Tooltip>
      )}

      {/* Decommissioned badge */}
      {isDecommissioned && (
        <Box
          sx={{
            position: "absolute",
            top: "12px",
            left: "12px",
            fontSize: "11px",
            fontWeight: 600,
            color: "#6B7280",
            backgroundColor: "#F3F4F6",
            border: "1px solid #D1D5DB",
            borderRadius: "10px",
            padding: "2px 8px",
            lineHeight: "16px",
            zIndex: 1,
          }}
        >
          {translate("decommissioned")}
        </Box>
      )}

      {showActions && (
        <Box className="flex justify-between items-center">
          <Box>
            {!machine.serialNumber &&
              <p className="text-14-medium" style={{ color: "#F23E3E" }}>
                {translate("machineSnMissing")}
              </p>}
          </Box>
          <Box className="flex items-center gap-2" onClick={stopProp}>
            {/* WhatsApp sunset 2026-03-14 — channel deactivated, web chat is sole channel */}
            {/* {machine.botActivated && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onWhatsAppBot?.(machine);
                }}
                sx={{
                  padding: "4px",
                  "&:hover": {
                    backgroundColor: "transparent"
                  }
                }}
              >
                <img src={whatsappLogo} alt="WhatsApp" style={{ width: 32, height: 32 }} />
              </IconButton>
            )} */}
            <TableActionButton stopPropagation>
            <MenuItem onClick={(e) => { e.stopPropagation(); navigate(`/machinery/${machine.id}`); }}>
              <TuneOutlined />
              {translate("profile")}
            </MenuItem>

            {isDecommissioned ? (
              <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                <MenuItem onClick={(e) => { e.stopPropagation(); onRestore?.(machine); }} sx={{ color: "#22C55E" }}>
                  <RestoreOutlined sx={{ color: "#22C55E" }} />
                  {translate("restore")}
                </MenuItem>
              </PermissionGuard>
            ) : (
              <>
                <MenuItem onClick={(e) => { e.stopPropagation(); onQRCode?.(machine); }}>
                  <QrCodeIcon />
                  {translate("qrCode")}
                </MenuItem>

                <MenuItem onClick={(e) => { e.stopPropagation(); onCreateServiceReport?.(machine); }}>
                  <BuildOutlined />
                  {translate("createServiceReport")}
                </MenuItem>

                <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                  <MenuItem onClick={(e) => { e.stopPropagation(); onCreateServiceRequest?.(machine); }}>
                    <BuildOutlined />
                    {translate("createServiceRequest")}
                  </MenuItem>
                </PermissionGuard>

                <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                  <MenuItem onClick={(e) => { e.stopPropagation(); (onEditDetails || onEdit)?.(machine); }}>
                    <EditOutlinedIcon />
                    {translate("editDetails")}
                  </MenuItem>
                </PermissionGuard>

                <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                  <MenuItem onClick={(e) => { e.stopPropagation(); onDuplicate?.(machine); }}>
                    <ContentCopy />
                    {translate("duplicate")}
                  </MenuItem>
                </PermissionGuard>

                <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                  {onAssignGroup && (
                    <MenuItem onClick={(e) => { e.stopPropagation(); onAssignGroup(machine); }}>
                      <GroupWorkIcon />
                      {translate("assignToGroup")}
                    </MenuItem>
                  )}
                </PermissionGuard>

                <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                  <MenuItem
                    onClick={(e) => { e.stopPropagation(); onDecommission?.(machine); }}
                    sx={{ color: "#F39C12" }}
                  >
                    <ArchiveOutlined sx={{ color: "#F39C12" }} />
                    {translate("decommission")}
                  </MenuItem>
                </PermissionGuard>
              </>
            )}
          </TableActionButton>
          </Box>
        </Box>
      )}

      <Box className="flex justify-center">
        <Box
          className="flex items-center justify-center"
          sx={{
            width: "100px",
            aspectRatio: 1,
            border: "1px solid #EEEEEF",
            borderRadius: "8px",
            filter: isDecommissioned ? "grayscale(40%)" : "none",
            transition: "filter 0.2s ease",
          }}
        >
          {(machine?.image?.content || machine?.catalogModel?.imageStandardUrl || machine?.catalogModel?.imageUrl) ? (
            <img
              className="object-cover w-full h-full"
              style={{ borderRadius: "7px" }}
              src={machine?.image?.content || machine?.catalogModel?.imageStandardUrl || machine?.catalogModel?.imageUrl || undefined}
              alt=""
            />
          ) : (
            <CameraIcon />
          )}
        </Box>
      </Box>

      <Box className="text-center" sx={{ width: "100%" }}>
        <Box className="text-18-bold max-w-[98%] text-wrap break-words" color="#333333">
          {getMachineDisplayName(machine)}
        </Box>
        <Box className="text-12-regular w-full" color="#333333" sx={{ minHeight: "18px" }}>
          {getMachineName(machine)}
        </Box>
        <Box className="text-12-regular w-full" color="#333333" sx={{ minHeight: "18px", width: "100%" }}>
          {getProductionYearDisplay(machine)}
        </Box>
      </Box>

      {/* Location */}
      {(() => {
        const locationDisplay = machine.locationName
          ? machine.parentLocationName
            ? `${machine.parentLocationName} > ${machine.locationName}`
            : machine.locationName
          : null;
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", mb: "4px" }}>
            <PlaceOutlined sx={{ fontSize: "14px", color: locationDisplay ? "#3498DB" : "#999", flexShrink: 0 }} />
            <Box sx={{
              fontSize: "12px", fontWeight: locationDisplay ? 500 : 400,
              color: locationDisplay ? "#666" : "#999",
              fontStyle: locationDisplay ? "normal" : "italic",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {locationDisplay || translate("locationNotSet")}
            </Box>
          </Box>
        );
      })()}

      <Box className="flex mt-4" >
        <Box className="text-14-medium w-1/3" color={machine.serialNumber ? "#333333" : "#F23E3E"}>
          {translate("sn")}
        </Box>
        <Box className="text-14-medium w-2/3" color="#333333">
          <OverflowTooltip text={machine?.serialNumber} />
        </Box>
      </Box>

      <Box className="flex" sx={{ marginTop: "-0.5rem" }}>
        <Box className="text-14-regular w-1/3">
          {translate("machine")}
        </Box>
        <Box className="text-14-regular w-2/3">
          <OverflowTooltip text={"#" + machine?.internalNumber} />
        </Box>
      </Box>

      <Box className="flex mt-4">
        <Box className="text-14-regular w-1/3" color="#333333">
          {tCommon("brand")}
        </Box>
        <Box className="w-2/3" sx={{ minHeight: "40px" }}>
          <Box className="text-14-medium" color="#333333">
            {getBrandDisplayName(machine)}
          </Box>
          <Box className="text-14-regular">{machine?.type.name}</Box>
        </Box>
      </Box>

      <Box className="flex mt-2">
        <Box className="text-14-regular w-1/3" color="#333333">
          {translate("system")}
        </Box>
        <Box className="w-2/3" sx={{ minHeight: "40px" }}>
          <Box className="text-14-medium" color="#333333">
            <OverflowTooltip text={machine?.controlSystemBrand?.name ? machine.controlSystemBrand.name : "\u2014"} className="text-14-medium" color={"#333333"} />
          </Box>
          <Box className="text-14-regular">
            <OverflowTooltip text={machine?.system ? machine.system : "\u2014"} className="text-14-medium" />
          </Box>
        </Box>
      </Box>

      {/* Service request status badge -- hidden for decommissioned, optionally hidden via prop */}
      {!hideServiceBadge && !isDecommissioned && machine.activeServiceRequestCount != null && machine.activeServiceRequestCount > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: "12px" }}>
          <SrStatusBadge
            machineId={machine.id}
            serviceRequestStatus={machine.serviceRequestStatus}
            serviceRequestStatusColor={machine.serviceRequestStatusColor}
            activeServiceRequestCount={machine.activeServiceRequestCount}
          />
        </Box>
      )}

      {/* Decommissioned date */}
      {isDecommissioned && machine.decommissionedDate && (
        <Box sx={{ textAlign: "center", mt: "12px", fontSize: "11px", fontFamily: 'var(--font-mono)', color: "#6B7280" }}>
          {translate("decommissionedOn", { date: new Date(machine.decommissionedDate).toLocaleDateString() })}
        </Box>
      )}
      {/* Decommission reason */}
      {isDecommissioned && machine.decommissionReason && (
        <Box sx={{
          textAlign: "center", mt: "4px", fontSize: "11px", fontStyle: "italic",
          color: "#9CA3AF", overflow: "hidden", textOverflow: "ellipsis",
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
        }}>
          &ldquo;{machine.decommissionReason}&rdquo;
        </Box>
      )}
    </Box>
  );
};

export default MachineCard;
